import React, { useEffect, useState } from 'react'
import { apiFetch } from '../../api.js'

const FRENCH_HOLIDAYS_2025 = [
  { date: '2025-01-01', label: 'Jour de l\'An' },
  { date: '2025-04-18', label: 'Vendredi Saint' },
  { date: '2025-04-21', label: 'Lundi de Pâques' },
  { date: '2025-05-01', label: 'Fête du Travail' },
  { date: '2025-05-08', label: 'Victoire 1945' },
  { date: '2025-05-29', label: 'Ascension' },
  { date: '2025-06-09', label: 'Lundi de Pentecôte' },
  { date: '2025-07-14', label: 'Fête Nationale' },
  { date: '2025-08-15', label: 'Assomption' },
  { date: '2025-11-01', label: 'Toussaint' },
  { date: '2025-11-11', label: 'Armistice' },
  { date: '2025-12-25', label: 'Noël' },
]

export default function SettingsHR({ onFlash }) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [section, setSection] = useState('conges')

  // Politique de congés
  const [defaultAnnualDays, setDefaultAnnualDays] = useState(25)
  const [minNoticeDays, setMinNoticeDays] = useState(7)
  const [allowCarryOver, setAllowCarryOver] = useState(false)
  const [maxCarryOverDays, setMaxCarryOverDays] = useState(5)
  const [autoApproveLeave, setAutoApproveLeave] = useState(false)

  // Jours fériés
  const [holidays, setHolidays] = useState([])
  const [newHolidayDate, setNewHolidayDate] = useState('')
  const [newHolidayLabel, setNewHolidayLabel] = useState('')

  // Paie
  const [currency, setCurrency] = useState('EUR')
  const [salaryPaymentDay, setSalaryPaymentDay] = useState(28)
  const [employerChargesPct, setEmployerChargesPct] = useState(42)

  // Infos société RH
  const [siret, setSiret] = useState('')
  const [apeCode, setApeCode] = useState('')
  const [collectiveAgreement, setCollectiveAgreement] = useState('')

  useEffect(() => {
    apiFetch('/api/settings/hr')
      .then((d) => {
        setDefaultAnnualDays(d.default_annual_leave_days ?? 25)
        setMinNoticeDays(d.min_notice_days ?? 7)
        setAllowCarryOver(!!d.allow_carry_over)
        setMaxCarryOverDays(d.max_carry_over_days ?? 5)
        setAutoApproveLeave(!!d.auto_approve_leave)
        setHolidays(d.public_holidays || [])
        setCurrency(d.currency || 'EUR')
        setSalaryPaymentDay(d.salary_payment_day ?? 28)
        setEmployerChargesPct(d.employer_charges_pct ?? 42)
        setSiret(d.siret || '')
        setApeCode(d.ape_code || '')
        setCollectiveAgreement(d.collective_agreement || '')
      })
      .catch(() => onFlash?.('❌ Impossible de charger les paramètres RH'))
      .finally(() => setLoading(false))
  }, [])

  async function handleSave(e) {
    e.preventDefault()
    setSaving(true)
    try {
      await apiFetch('/api/settings/hr', {
        method: 'PATCH',
        body: {
          default_annual_leave_days: Number(defaultAnnualDays),
          min_notice_days: Number(minNoticeDays),
          allow_carry_over: allowCarryOver,
          max_carry_over_days: Number(maxCarryOverDays),
          auto_approve_leave: autoApproveLeave,
          public_holidays: holidays,
          currency,
          salary_payment_day: Number(salaryPaymentDay),
          employer_charges_pct: Number(employerChargesPct),
          siret,
          ape_code: apeCode,
          collective_agreement: collectiveAgreement,
        },
      })
      onFlash?.('Paramètres RH sauvegardés.')
    } catch (err) {
      onFlash?.('❌ Erreur : ' + (err?.message || 'Erreur'))
    } finally {
      setSaving(false)
    }
  }

  function addHoliday() {
    if (!newHolidayDate || !newHolidayLabel.trim()) return
    if (holidays.find((h) => h.date === newHolidayDate)) {
      onFlash?.('Ce jour férié existe déjà.')
      return
    }
    const updated = [...holidays, { date: newHolidayDate, label: newHolidayLabel.trim() }]
      .sort((a, b) => a.date.localeCompare(b.date))
    setHolidays(updated)
    setNewHolidayDate('')
    setNewHolidayLabel('')
  }

  function removeHoliday(date) {
    setHolidays(holidays.filter((h) => h.date !== date))
  }

  function importFrenchHolidays() {
    const merged = [...holidays]
    for (const h of FRENCH_HOLIDAYS_2025) {
      if (!merged.find((x) => x.date === h.date)) merged.push(h)
    }
    setHolidays(merged.sort((a, b) => a.date.localeCompare(b.date)))
  }

  if (loading) return <div className="p-4 text-slate-500 text-sm">Chargement...</div>

  const SECTIONS = [
    { key: 'conges', label: 'Politique de congés' },
    { key: 'feries', label: 'Jours fériés' },
    { key: 'paie', label: 'Paie' },
    { key: 'societe', label: 'Infos société' },
  ]

  return (
    <form onSubmit={handleSave} className="space-y-6 max-w-2xl">
      {/* Sous-navigation */}
      <div className="flex gap-1 flex-wrap border-b border-border pb-2">
        {SECTIONS.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setSection(s.key)}
            className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
              section === s.key
                ? 'bg-indigo-600 text-white'
                : 'text-content-secondary hover:bg-surface-secondary'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* ── Politique de congés ── */}
      {section === 'conges' && (
        <div className="space-y-5">
          <h3 className="text-sm font-semibold text-content-primary">Politique de congés</h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-content-secondary mb-1">
                Jours annuels par défaut
              </label>
              <input
                type="number"
                min="0" max="365"
                value={defaultAnnualDays}
                onChange={(e) => setDefaultAnnualDays(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-surface-primary text-content-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-xs text-content-tertiary mt-1">Appliqué aux nouveaux employés</p>
            </div>

            <div>
              <label className="block text-xs font-medium text-content-secondary mb-1">
                Préavis minimum (jours)
              </label>
              <input
                type="number"
                min="0" max="90"
                value={minNoticeDays}
                onChange={(e) => setMinNoticeDays(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-surface-primary text-content-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-xs text-content-tertiary mt-1">Délai avant la date de départ</p>
            </div>
          </div>

          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={allowCarryOver}
                onChange={(e) => setAllowCarryOver(e.target.checked)}
                className="w-4 h-4 text-indigo-600 rounded"
              />
              <span className="text-sm text-content-primary">Autoriser le report des jours non pris</span>
            </label>

            {allowCarryOver && (
              <div className="ml-7">
                <label className="block text-xs font-medium text-content-secondary mb-1">
                  Maximum de jours reportables
                </label>
                <input
                  type="number"
                  min="0" max="60"
                  value={maxCarryOverDays}
                  onChange={(e) => setMaxCarryOverDays(e.target.value)}
                  className="w-28 px-3 py-2 text-sm border border-border rounded-lg bg-surface-primary text-content-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )}

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={autoApproveLeave}
                onChange={(e) => setAutoApproveLeave(e.target.checked)}
                className="w-4 h-4 text-indigo-600 rounded"
              />
              <span className="text-sm text-content-primary">Approbation automatique des demandes</span>
            </label>
            {autoApproveLeave && (
              <p className="ml-7 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-1.5">
                Toutes les demandes seront approuvées automatiquement sans validation manuelle.
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── Jours fériés ── */}
      {section === 'feries' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-content-primary">Jours fériés</h3>
            <button
              type="button"
              onClick={importFrenchHolidays}
              className="text-xs px-3 py-1.5 border border-indigo-300 text-indigo-600 rounded-md hover:bg-indigo-50 transition-colors"
            >
              Importer jours fériés français 2025
            </button>
          </div>

          <p className="text-xs text-content-tertiary">
            Ces jours sont exclus du décompte des congés.
          </p>

          {/* Liste */}
          <div className="border border-border rounded-lg overflow-hidden">
            {holidays.length === 0 ? (
              <div className="p-4 text-sm text-content-secondary text-center">
                Aucun jour férié configuré
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-surface-secondary border-b border-border">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs font-medium text-content-secondary">Date</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-content-secondary">Libellé</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {holidays.map((h) => (
                    <tr key={h.date} className="border-b border-border last:border-0 hover:bg-surface-secondary transition-colors">
                      <td className="px-3 py-2 text-content-primary font-mono text-xs">{h.date}</td>
                      <td className="px-3 py-2 text-content-primary">{h.label}</td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => removeHoliday(h.date)}
                          className="text-red-400 hover:text-red-600 transition-colors text-xs"
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Ajouter */}
          <div className="flex gap-2 items-end">
            <div>
              <label className="block text-xs font-medium text-content-secondary mb-1">Date</label>
              <input
                type="date"
                value={newHolidayDate}
                onChange={(e) => setNewHolidayDate(e.target.value)}
                className="px-3 py-2 text-sm border border-border rounded-lg bg-surface-primary text-content-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-content-secondary mb-1">Libellé</label>
              <input
                type="text"
                value={newHolidayLabel}
                onChange={(e) => setNewHolidayLabel(e.target.value)}
                placeholder="ex: Noël"
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-surface-primary text-content-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <button
              type="button"
              onClick={addHoliday}
              className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Ajouter
            </button>
          </div>
        </div>
      )}

      {/* ── Paie ── */}
      {section === 'paie' && (
        <div className="space-y-5">
          <h3 className="text-sm font-semibold text-content-primary">Paramètres de paie</h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-content-secondary mb-1">Devise</label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-surface-primary text-content-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="EUR">EUR — Euro</option>
                <option value="USD">USD — Dollar US</option>
                <option value="GBP">GBP — Livre sterling</option>
                <option value="MAD">MAD — Dirham marocain</option>
                <option value="DZD">DZD — Dinar algérien</option>
                <option value="TND">TND — Dinar tunisien</option>
                <option value="XOF">XOF — Franc CFA</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-content-secondary mb-1">
                Jour de versement des salaires
              </label>
              <input
                type="number"
                min="1" max="31"
                value={salaryPaymentDay}
                onChange={(e) => setSalaryPaymentDay(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-surface-primary text-content-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-xs text-content-tertiary mt-1">Jour du mois (1–31)</p>
            </div>
          </div>

          <div className="max-w-xs">
            <label className="block text-xs font-medium text-content-secondary mb-1">
              Charges patronales (%)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0" max="100"
                step="0.1"
                value={employerChargesPct}
                onChange={(e) => setEmployerChargesPct(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-surface-primary text-content-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <span className="text-sm text-content-secondary">%</span>
            </div>
            <p className="text-xs text-content-tertiary mt-1">Taux appliqué au brut pour le coût patronal total</p>
          </div>
        </div>
      )}

      {/* ── Informations société RH ── */}
      {section === 'societe' && (
        <div className="space-y-5">
          <h3 className="text-sm font-semibold text-content-primary">Informations société (volet RH)</h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-content-secondary mb-1">SIRET</label>
              <input
                type="text"
                value={siret}
                onChange={(e) => setSiret(e.target.value.replace(/\s/g, '').slice(0, 14))}
                placeholder="12345678901234"
                maxLength={14}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-surface-primary text-content-primary font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-xs text-content-tertiary mt-1">14 chiffres sans espaces</p>
            </div>

            <div>
              <label className="block text-xs font-medium text-content-secondary mb-1">Code APE / NAF</label>
              <input
                type="text"
                value={apeCode}
                onChange={(e) => setApeCode(e.target.value.slice(0, 10))}
                placeholder="6201Z"
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-surface-primary text-content-primary font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-content-secondary mb-1">
              Convention collective
            </label>
            <input
              type="text"
              value={collectiveAgreement}
              onChange={(e) => setCollectiveAgreement(e.target.value.slice(0, 200))}
              placeholder="ex: Syntec (IDCC 1486)"
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-surface-primary text-content-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 pt-2 border-t border-border">
        <button
          type="submit"
          disabled={saving}
          className="px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
        >
          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </button>
        <p className="text-xs text-content-tertiary">Les modifications s'appliquent à toute l'organisation.</p>
      </div>
    </form>
  )
}
